import { z } from 'zod';
import type { MediaNode } from './modelTypes/media';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lateinit = null as any;

export function unreachable(value: never, template: string = 'Unhandled case: %'): never {
	if (value !== undefined) {
		throw new Error(template.replace('%', value).trim());
	} else {
		throw new Error(template.replace('%', '').trim());
	}
}

export function softUnreachable(value: never, template: string = 'Unhandled case: %') {
	if (value !== undefined) {
		console.error(template.replace('%', value).trim());
	} else {
		console.error(template.replace('%', '').trim());
	}
}

export function delayMs(delay: number): Promise<void> {
	return new Promise((resolve, _reject) => {
		setTimeout(() => {
			resolve();
		}, delay);
	});
}

export function toTitleCase(str: string) {
	return str
		.split(' ')
		.map((word) => word[0].toUpperCase() + word.slice(1))
		.join(' ');
}

export function toSentenceCase(str: string) {
	return str[0].toUpperCase() + str.slice(1);
}

export type JSONValue = string | number | boolean | null | JSONObject | JSONArray;

export interface JSONObject {
	[key: string]: JSONValue;
}

export interface JSONArray extends Array<JSONValue> {}

export const Random = {
	range(from = 0, to = 1) {
		return from + Math.random() * (to - from);
	},

	rangeInt(from: number, to: number) {
		return Math.floor(Random.range(from, to));
	},

	fromString(s: string, count = 1) {
		let acc = '';

		for (let i = 0; i < count; i++) {
			const index = Random.rangeInt(0, s.length);
			const c = s[index];
			acc += c;
		}
		return acc;
	},

	fromArray<T>(arr: T[]): T {
		const i = Random.rangeInt(0, arr.length);
		return arr[i] as T;
	},

	fromTuple<const T extends readonly unknown[]>(arr: T): T[number] {
		const i = Random.rangeInt(0, arr.length);
		return arr[i] as T[number];
	},

	chance(threshold: number) {
		return Math.random() < threshold;
	},
};

export function splitName(
	fullName: string,
	domain: string | undefined = undefined,
): { firstName: string; lastName: string; email: string } {
	// Split the full name into an array of strings
	const nameParts = fullName.split(' ');
	if (nameParts.length === 1) {
		const email = fullName.toLowerCase() + '@' + (domain || 'example.com');

		return {
			firstName: fullName,
			lastName: '',
			email: email,
		};
	}

	// Check if the last part is a common prefix or particle
	const particles = [
		'de',
		'di',
		'do',
		'der',
		'den',
		'del',
		'des',
		'dal',
		'du',
		'la',
		'le',
		'los',
		'les',
		'van',
		'op',
		'ten',
		'ter',
		'het',
	];
	let lastNameIndex = nameParts.length - 1;
	while (lastNameIndex > 0 && particles.includes((nameParts[lastNameIndex - 1] || '').toLowerCase())) {
		lastNameIndex--;
	}
	const lastName = nameParts.splice(lastNameIndex).join(' ');
	const firstName = nameParts.join(' ');
	const email = (firstName[0]?.toLowerCase() || 'x') + '.' + lastName.toLowerCase().replace(/\s/g, '') + '@' + (domain || 'example.com');

	return {
		firstName: firstName,
		lastName: lastName,
		email: email,
	};
}

/**
 * Creates a debounced function that delays invoking the provided function until
 * after the specified duration has elapsed since the last time the debounced function was invoked.
 *
 * @template T - The type of the function to debounce.
 * @param {T} fn - The function to debounce.
 * @param {number} durationMS - The number of milliseconds to delay invocation.
 * @returns {(...args: Parameters<T>) => void} A new debounced function that delays invoking `fn`.
 *
 * @example
 * const logMessage = (message: string) => {
 *   console.log(message);
 * };
 *
 * const debouncedLogMessage = debounce(logMessage, 1000);
 *
 * debouncedLogMessage('Hello, World!'); // Will log 'Hello, World!' to the console after 1 second if not called again within that time.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(fn: T, durationMS: number): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	return function (...args: Parameters<T>): void {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			fn(...args);
		}, durationMS);
	};
}

export function downloadString(content: string, filename: string, contentType: string) {
	const blob = new Blob([content], { type: contentType });
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export function generateUUID(): string {
	// cspell:disable
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
		const random = (Math.random() * 16) | 0;
		const value = char === 'x' ? random : (random & 0x3) | 0x8;
		return value.toString(16);
	});
	// cspell:enable
}

export function prefersReducedMotion() {
	return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function shuffle<T>(arr: T[]) {
	return arr.sort(() => Math.random() * 2 - 1);
}

export function deepClone<T extends JSONValue>(v: T): T {
	return JSON.parse(JSON.stringify(v));
}

export function clamp(v: number, a: number, b: number) {
	let min: number;
	let max: number;
	if (a > b) {
		max = a;
		min = b;
	} else {
		min = a;
		max = b;
	}
	return Math.min(max, Math.max(v, min));
}

export function lerpUnclamped(a: number, b: number, t: number): number {
	return (1 - t) * a + t * b;
}

export function lerp(a: number, b: number, t: number): number {
	return lerpUnclamped(a, b, clamp(t, 0, 1));
}

export function invLerpUnclamped(p: number, a: number, b: number) {
	const v = (p - a) / (b - a);
	return isNaN(v) ? 0 : v;
}

export function invLerp(p: number, a: number, b: number) {
	return invLerpUnclamped(clamp(p, a, b), a, b);
}

export function unique<T extends object>(uniqueKeys: { key: keyof T; message: string }[]) {
	return (data: T[], ctx: z.RefinementCtx) => {
		uniqueKeys.forEach(({ key, message }) => {
			const seen = new Set();
			data.forEach((item, index) => {
				const value = item[key];
				if (seen.has(value)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message,
						path: [index, key.toString()], // .toString() in case `key` is typeof `symbol`.
					});
				} else {
					seen.add(value);
				}
			});
		});
	};
}

export function getStorageUrl(node: MediaNode, storageZone: string) {
	const ext = node.filename.split('.').at(-1) ?? '';
	const filename = ext.length == 0 ? node.id : `${node.id}.${ext}`;

	const url = `https://${storageZone}/${filename}`;
	return url;
}

/**
 * Calculate the haversine distance between two points on the Earth's surface
 * @param lat1 - The latitude of the first point
 * @param lon1 - The longitude of the first point
 * @param lat2 - The latitude of the second point
 * @param lon2 - The longitude of the second point
 * @returns The distance in meters
 */
export function haversineDistance(p1: { lat: number; lng: number }, p2: { lat: number; lng: number }): number {
	const R = 6371000; // Earth radius in meters
	const deg2Rad = Math.PI / 180;

	const dLat = (p2.lat - p1.lat) * deg2Rad;
	const dLon = (p2.lng - p1.lng) * deg2Rad;
	const a = Math.sin(dLat / 2) ** 2 + Math.cos(p1.lat * deg2Rad) * Math.cos(p2.lat * deg2Rad) * Math.sin(dLon / 2) ** 2;
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

export function isPast(date: Date | string) {
	const compareDate = date instanceof Date ? date : new Date(date);
	return compareDate < new Date();
}

/**
 * Creates a new title by incrementing the number at the end of the title.
 * @param title - The title to copy.
 * @returns The new title of the copy.
 */
export function copyTitle(title: string) {
	const endsWithNumber = title.match(/\d+$/);
	if (!endsWithNumber) {
		return title + ' - 2';
	}

	const number = parseInt(endsWithNumber[0]);
	const titleWithNumberIncremented = title.replace(/\d+$/, () => String(number + 1));

	return titleWithNumberIncremented;
}
export function truncate(text: string, maxLength: number) {
	if (text.length <= maxLength) return text;
	return text.slice(0, maxLength) + '...';
}
